import { useCallback, useState } from 'react'

import type { ReaderLayoutStore } from '@/store/useReaderLayoutStore'

/**
 * 作品ごとに覚えたリーダーの表示設定を扱うフック。
 *
 * initialSettings は 作品ごとの設定 → 最後に選んだ設定 → base の順に決め、マウント中は変えない。
 * 初めて開く作品では isPromptOpen が true になる。
 */
export const useRememberedLayout = <S extends object, L extends Partial<S>>(
  store: ReaderLayoutStore<L>,
  productId: number | undefined,
  base: S,
) => {
  const [initialSettings] = useState<S>(() => {
    const { productLayouts, lastLayout } = store.getState()
    const layout = (productId !== undefined ? productLayouts[productId] : undefined) ?? lastLayout
    return { ...base, ...layout }
  })
  const [isPromptOpen, setIsPromptOpen] = useState(
    () => productId !== undefined && !store.getState().productLayouts[productId],
  )
  // store を hook として呼ぶと、名前が use で始まらないため React Compiler が呼び出しをメモ化して
  // 2 回目以降のレンダーで内部の hook を飛ばし、hook の順序が崩れる。getState() から呼ぶ
  const save = useCallback((layout: L) => store.getState().saveLayout(productId, layout), [store, productId])
  const closePrompt = useCallback(() => setIsPromptOpen(false), [])

  return { initialSettings, isPromptOpen, closePrompt, save }
}
